export type StorybookRuleOrientation = 'horizontal' | 'vertical'

export interface StorybookRule {
  orientation: StorybookRuleOrientation
  offset: number
}

const RULE_SEPARATOR = ','
const RULE_PATTERN = /^([hv])(\d+)$/u
const INITIAL_RULE_OFFSET = 100
const RULE_OFFSET_STEP = 16

export function parseStorybookRules(value: string | null): StorybookRule[] {
  if (!value) return []

  const rules: StorybookRule[] = []
  const ids = new Set<string>()

  for (const encodedRule of value.split(RULE_SEPARATOR)) {
    const match = RULE_PATTERN.exec(encodedRule)
    if (!match) continue

    const orientation = match[1] === 'v' ? 'vertical' : 'horizontal'
    const offset = Number(match[2])
    const id = getStorybookRuleId({ orientation, offset })
    if (!Number.isSafeInteger(offset) || ids.has(id)) continue

    ids.add(id)
    rules.push({ orientation, offset })
  }

  return rules
}

export function serializeStorybookRules(rules: StorybookRule[]) {
  return rules
    .map((rule) => {
      const orientation = rule.orientation === 'vertical' ? 'v' : 'h'
      return `${orientation}${normalizeRuleOffset(rule.offset)}`
    })
    .join(RULE_SEPARATOR)
}

export function getStorybookRuleId(rule: StorybookRule) {
  return `${rule.orientation}:${rule.offset}`
}

export function addStorybookRule(
  rules: StorybookRule[],
  orientation: StorybookRuleOrientation,
) {
  const offsets = rules
    .filter((rule) => rule.orientation === orientation)
    .map((rule) => rule.offset)
  const desiredOffset =
    offsets.length === 0
      ? INITIAL_RULE_OFFSET
      : Math.max(...offsets) + RULE_OFFSET_STEP

  return [
    ...rules,
    {
      orientation,
      offset: getAvailableRuleOffset(rules, orientation, desiredOffset),
    },
  ]
}

export function getAvailableRuleOffset(
  rules: StorybookRule[],
  orientation: StorybookRuleOrientation,
  desiredOffset: number,
  maximumOffset?: number,
) {
  const occupiedOffsets = new Set(
    rules
      .filter((rule) => rule.orientation === orientation)
      .map((rule) => rule.offset),
  )
  const maximum = Math.max(0, Math.floor(maximumOffset ?? Infinity))
  let offset = Math.min(normalizeRuleOffset(desiredOffset), maximum)

  while (occupiedOffsets.has(offset) && offset < maximum) offset += 1
  while (occupiedOffsets.has(offset) && offset > 0) offset -= 1

  return offset
}

function normalizeRuleOffset(offset: number) {
  if (!Number.isFinite(offset)) return 0
  return Math.max(0, Math.round(offset))
}
